class TestimonialSpotlight extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.swiper = null;
    this.thumbButtons = [];
    this.progressDots = [];
    this.counterElement = null;
    this.slideItems = [];
    this.prevButton = null;
    this.nextButton = null;
    this.currentIndex = 0;
    this.thumbRail = null;
    this.thumbsWrap = null;
    this.thumbRing = null;
    this.thumbRingObserver = null;
    this.thumbRingScrollHandler = null;
    this.thumbRingPlacementFrame = null;
    this.pendingFrame = null;

    this.boundThumbClick = this.onThumbClick.bind(this);
    this.boundSlideChange = this.onSlideChange.bind(this);
    this.boundAttachWhenReady = this.attachWhenReady.bind(this);
    this.boundWindowResize = this.onWindowResize.bind(this);
  }

  connectedCallback() {
    this.slider = this.querySelector('testimonial-slider, grid-slider');
    this.thumbRail = this.querySelector('.testimonials__thumbs');
    this.thumbsWrap = this.querySelector('.testimonials__thumbs-wrap');
    this.thumbRing = this.querySelector('[data-testimonial-thumb-ring]');
    this.thumbButtons = Array.from(this.querySelectorAll('[data-testimonial-thumb]'));
    this.progressDots = Array.from(this.querySelectorAll('[data-testimonial-progress] .testimonials__voices-progress-dot'));
    this.counterElement = this.querySelector('[data-testimonial-counter]');
    this.slideItems = Array.from(this.querySelectorAll('.grid-slider__item'));

    if (!(this.slider instanceof HTMLElement) || this.slideItems.length === 0) return;

    const prevId = this.slider.dataset.navPrev || '';
    const nextId = this.slider.dataset.navNext || '';
    this.prevButton = prevId
      ? document.getElementById(prevId)
      : this.querySelector('.grid-slider__btn--prev');
    this.nextButton = nextId
      ? document.getElementById(nextId)
      : this.querySelector('.grid-slider__btn--next');

    this.prepareQuoteWordReveal(this);

    if (this.thumbButtons.length > 0) {
      this.thumbButtons.forEach((button) => {
        button.addEventListener('click', this.boundThumbClick);
      });
      this.bindThumbRingObservers();
    }

    if (this.slideItems.length <= 1) {
      this.setActiveSlide(0);
      return;
    }

    this.attachWhenReady();
  }

  disconnectedCallback() {
    this.thumbButtons.forEach((button) => {
      button.removeEventListener('click', this.boundThumbClick);
    });
    this.thumbButtons = [];

    if (this.pendingFrame) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }

    if (this.thumbRingPlacementFrame) {
      cancelAnimationFrame(this.thumbRingPlacementFrame);
      this.thumbRingPlacementFrame = null;
    }

    if (this.thumbRingObserver) {
      this.thumbRingObserver.disconnect();
      this.thumbRingObserver = null;
    }

    if (this.thumbsWrap && this.thumbRingScrollHandler) {
      this.thumbsWrap.removeEventListener('scroll', this.thumbRingScrollHandler);
    }
    this.thumbRingScrollHandler = null;

    window.removeEventListener('resize', this.boundWindowResize);

    if (this.swiper) {
      this.swiper.off('slideChange', this.boundSlideChange);
      this.swiper.off('snapIndexChange', this.boundSlideChange);
    }

    this.swiper = null;
    this.slider = null;
    this.slideItems = [];
    this.prevButton = null;
    this.nextButton = null;
    this.currentIndex = 0;
    this.thumbRail = null;
    this.thumbsWrap = null;
    this.thumbRing = null;
    this.progressDots = [];
    this.counterElement = null;
  }

  attachWhenReady() {
    if (!this.isConnected) return;
    if (!(this.slider instanceof HTMLElement)) return;

    const sliderInstance = this.slider.swiper;
    if (!sliderInstance) {
      this.pendingFrame = requestAnimationFrame(this.boundAttachWhenReady);
      return;
    }

    if (this.swiper && this.swiper === sliderInstance) {
      this.onSlideChange();
      return;
    }

    if (this.swiper) {
      this.swiper.off('slideChange', this.boundSlideChange);
      this.swiper.off('snapIndexChange', this.boundSlideChange);
    }

    this.swiper = sliderInstance;
    this.swiper.on('slideChange', this.boundSlideChange);
    this.swiper.on('snapIndexChange', this.boundSlideChange);
    // Treat the initial slide identically to every later slide change —
    // dots, counter, thumb, AND revealSlide() (which adds
    // `is-word-reveal-ready` to the host). Doing this via setActiveSlide
    // is critical: the previous code called onSlideChange() here, but
    // onSlideChange early-returns when activeIndex === currentIndex (both
    // are 0 on init), so the host never got `is-word-reveal-ready` until
    // the user manually changed slides. Without that class:
    //   • slide 0 had no word-reveal animation (felt "instant")
    //   • slides stayed in the swiper-initialized flex row instead of
    //     stacking position:absolute, which on mobile leaked intrinsic
    //     width up to the panel and caused horizontal overflow until
    //     the user nudged a slide.
    const initialIndex = this.getCurrentIndex();
    this.setActiveSlide(initialIndex);
  }

  onThumbClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const slideIndex = parseInt(button.dataset.index || '', 10);
    if (!Number.isFinite(slideIndex)) return;

    if (this.swiper) {
      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(slideIndex);
      } else {
        this.swiper.slideTo(slideIndex);
      }
      return;
    }

    this.setActiveSlide(slideIndex);
  }

  normalizeIndex(index) {
    const count = this.slideItems.length;
    if (count <= 0) return 0;
    const mod = index % count;
    return mod < 0 ? mod + count : mod;
  }

  setActiveSlide(index) {
    const safeIndex = this.normalizeIndex(index);
    this.currentIndex = safeIndex;
    this.updateThumbState(safeIndex);
    this.updateProgressDots(safeIndex);
    this.updateCounter(safeIndex);

    const slide = this.findSlideBySourceIndex(safeIndex) || this.slideItems[safeIndex];
    if (slide instanceof HTMLElement) {
      this.revealSlide(slide);
    }
  }

  updateThumbState(activeIndex) {
    if (this.thumbButtons.length === 0) return;

    this.thumbButtons.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');

      if (isActive) {
        this.scrollThumbIntoRail(button);
      }
    });

    this.placeThumbRing(activeIndex, true);
  }

  /**
   * Position the animated ring around the active avatar. Pure transform via
   * top/left/width/height — they're all transitioned in CSS so the ring
   * glides between avatars on click. Math compensates for the wrap's
   * scrollLeft so the ring stays anchored to the correct avatar even when
   * the rail is scrolled horizontally on narrow viewports.
   */
  placeThumbRing(index, animate = true) {
    if (!(this.thumbRing instanceof HTMLElement)) return;
    if (!(this.thumbsWrap instanceof HTMLElement)) return;

    const button = this.thumbButtons[index];
    if (!(button instanceof HTMLElement)) return;

    const wrapRect = this.thumbsWrap.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    // Avatar dimensions can be 0 if images haven't laid out yet — bail and
    // rely on the ResizeObserver to retry once layout is settled.
    if (buttonRect.width === 0 || buttonRect.height === 0) return;

    const GAP = 5;
    const scrollLeft = this.thumbsWrap.scrollLeft || 0;
    const scrollTop = this.thumbsWrap.scrollTop || 0;
    const size = buttonRect.width + GAP * 2;
    const top = buttonRect.top - wrapRect.top + scrollTop - GAP;
    const left = buttonRect.left - wrapRect.left + scrollLeft - GAP;

    const apply = () => {
      if (!(this.thumbRing instanceof HTMLElement)) return;
      this.thumbRing.style.width = `${size}px`;
      this.thumbRing.style.height = `${size}px`;
      this.thumbRing.style.top = `${top}px`;
      this.thumbRing.style.left = `${left}px`;
      this.thumbRing.setAttribute('data-placed', 'true');
    };

    if (animate) {
      apply();
      return;
    }

    // Initial / resize placement: suppress the transition for one frame so
    // the ring snaps without animating from its previous (or empty) state.
    this.thumbRing.style.transition = 'none';
    apply();
    // Force a reflow so the disabled transition takes effect, then restore
    // the original transition for subsequent updates.
    void this.thumbRing.offsetWidth;
    if (this.thumbRingPlacementFrame) {
      cancelAnimationFrame(this.thumbRingPlacementFrame);
    }
    this.thumbRingPlacementFrame = requestAnimationFrame(() => {
      this.thumbRingPlacementFrame = null;
      if (this.thumbRing instanceof HTMLElement) {
        this.thumbRing.style.transition = '';
      }
    });
  }

  bindThumbRingObservers() {
    if (!(this.thumbRing instanceof HTMLElement)) return;
    if (!(this.thumbsWrap instanceof HTMLElement)) return;

    // First placement after layout settles (rAF queues after paint, so any
    // CSS-driven sizing of the rail/buttons has applied by the time we read
    // bounding rects).
    requestAnimationFrame(() => {
      this.placeThumbRing(this.currentIndex, false);
    });

    // Recompute when avatar images load (they may resize the buttons).
    this.thumbButtons.forEach((button) => {
      const img = button.querySelector('img');
      if (img instanceof HTMLImageElement && !img.complete) {
        img.addEventListener('load', () => this.placeThumbRing(this.currentIndex, false), { once: true });
      }
    });

    // Recompute whenever the rail itself resizes (font load, viewport change,
    // container query, etc.). ResizeObserver is the modern, layout-aware
    // way to do this — far more robust than relying on window resize alone.
    if ('ResizeObserver' in window && this.thumbRail instanceof HTMLElement) {
      this.thumbRingObserver = new ResizeObserver(() => {
        this.placeThumbRing(this.currentIndex, false);
      });
      this.thumbRingObserver.observe(this.thumbRail);
    }

    window.addEventListener('resize', this.boundWindowResize, { passive: true });
  }

  onWindowResize() {
    this.placeThumbRing(this.currentIndex, false);
  }

  updateProgressDots(activeIndex) {
    if (this.progressDots.length === 0) return;

    this.progressDots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  }

  updateCounter(activeIndex) {
    if (!(this.counterElement instanceof HTMLElement)) return;
    const total = this.slideItems.length;
    if (total <= 0) return;
    const display = String(activeIndex + 1).padStart(2, '0');
    if (this.counterElement.textContent !== display) {
      this.counterElement.textContent = display;
    }
  }

  onSlideChange() {
    if (!this.swiper) return;

    // Defer one microtask so Swiper has committed realIndex and DOM classes
    Promise.resolve().then(() => {
      const activeIndex = this.getCurrentIndex();
      if (activeIndex === this.currentIndex) return;
      this.currentIndex = activeIndex;
      this.updateThumbState(this.currentIndex);
      this.updateProgressDots(this.currentIndex);
      this.updateCounter(this.currentIndex);

      const activeSlide =
        this.findDomActiveSlide() ||
        this.findSlideBySourceIndex(this.currentIndex) ||
        this.slideItems[this.currentIndex];
      this.revealSlide(activeSlide);
    });
  }

  findSlideBySourceIndex(index) {
    const normalizedIndex = this.normalizeIndex(index);

    if (this.slider instanceof HTMLElement) {
      const allSlides = Array.from(this.slider.querySelectorAll('.grid-slider__item'));
      const match = allSlides.find((slide) => {
        if (!(slide instanceof HTMLElement)) return false;
        return parseInt(slide.dataset.swiperSlideIndex || '', 10) === normalizedIndex;
      });
      if (match instanceof HTMLElement) return match;
    }

    return this.slideItems[normalizedIndex] instanceof HTMLElement
      ? this.slideItems[normalizedIndex]
      : null;
  }

  findDomActiveSlide() {
    if (!(this.slider instanceof HTMLElement)) return null;
    const activeSlides = Array.from(
      this.slider.querySelectorAll('.grid-slider__item.swiper-slide-active')
    );
    return activeSlides[0] instanceof HTMLElement ? activeSlides[0] : null;
  }

  readSlideSourceIndex(slide) {
    if (!(slide instanceof HTMLElement)) return null;
    const sourceIndex = parseInt(slide.dataset.swiperSlideIndex || '', 10);
    if (Number.isFinite(sourceIndex)) return sourceIndex;
    const fallbackIndex = this.slideItems.indexOf(slide);
    return fallbackIndex >= 0 ? fallbackIndex : null;
  }

  getCurrentIndex() {
    const activeSlide = this.findDomActiveSlide();
    const activeSlideIndex = this.readSlideSourceIndex(activeSlide);
    if (Number.isFinite(activeSlideIndex)) {
      return this.normalizeIndex(activeSlideIndex);
    }

    if (this.swiper && Number.isFinite(this.swiper.realIndex)) {
      return this.normalizeIndex(this.swiper.realIndex);
    }

    if (this.swiper && Number.isFinite(this.swiper.activeIndex)) {
      return this.normalizeIndex(this.swiper.activeIndex);
    }

    return this.normalizeIndex(this.currentIndex);
  }

  revealSlide(slide) {
    if (!(slide instanceof HTMLElement)) return;

    if (!this.classList.contains('is-word-reveal-ready')) {
      this.classList.add('is-word-reveal-ready');
    }

    this.prepareQuoteWordReveal(slide);

    const allSlides =
      this.slider instanceof HTMLElement
        ? Array.from(this.slider.querySelectorAll('.grid-slider__item'))
        : this.slideItems;

    allSlides.forEach((item) => {
      if (item instanceof HTMLElement) {
        item.classList.remove('is-revealing');
      }
    });

    const words = slide.querySelectorAll('.testimonial-word');
    words.forEach((word, index) => {
      if (word instanceof HTMLElement) {
        word.style.setProperty('--tw-index', String(index));
      }
    });

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      slide.classList.add('is-revealing');
      return;
    }

    void slide.offsetWidth;
    slide.classList.add('is-revealing');
  }

  prepareQuoteWordReveal(scope) {
    if (!(scope instanceof HTMLElement)) return;

    const quoteNodes = Array.from(scope.querySelectorAll('.testimonial-spotlight__quote'));
    quoteNodes.forEach((quoteNode) => {
      if (!(quoteNode instanceof HTMLElement)) return;
      if (quoteNode.dataset.wordRevealPrepared === 'true') return;
      this.wrapQuoteWords(quoteNode);
      quoteNode.dataset.wordRevealPrepared = 'true';
    });
  }

  wrapQuoteWords(container) {
    if (!(container instanceof HTMLElement)) return;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!(node instanceof Text)) return NodeFilter.FILTER_REJECT;
        if (!node.nodeValue || node.nodeValue.trim() === '') return NodeFilter.FILTER_REJECT;
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest('.testimonial-word')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const textNodes = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((node) => {
      if (!(node instanceof Text) || !(node.parentNode instanceof Node)) return;

      const fragment = document.createDocumentFragment();
      const tokens = node.nodeValue.split(/(\s+)/);

      tokens.forEach((token) => {
        if (!token) return;

        if (/^\s+$/.test(token)) {
          fragment.appendChild(document.createTextNode(token));
          return;
        }

        const word = document.createElement('span');
        word.className = 'testimonial-word';
        word.textContent = token;
        fragment.appendChild(word);
      });

      node.parentNode.replaceChild(fragment, node);
    });
  }

  scrollThumbIntoRail(button) {
    if (!(button instanceof HTMLElement)) return;

    const rail =
      this.thumbRail instanceof HTMLElement
        ? this.thumbRail
        : button.closest('.testimonials__thumbs');

    if (!(rail instanceof HTMLElement)) return;

    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    if (maxScrollLeft <= 0) return;

    const railRect = rail.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const centerOffset =
      buttonRect.left - railRect.left - (railRect.width - buttonRect.width) / 2;
    const targetLeft = Math.min(maxScrollLeft, Math.max(0, rail.scrollLeft + centerOffset));

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReducedMotion && typeof rail.scrollTo === 'function') {
      rail.scrollTo({ left: targetLeft, behavior: 'smooth' });
      return;
    }

    rail.scrollLeft = targetLeft;
  }
}

if (!customElements.get('testimonial-spotlight')) {
  customElements.define('testimonial-spotlight', TestimonialSpotlight);
}
